import { Badge } from '@sr/ui-library';
import { toTitleCase } from '../../utils/helpers/common';
import { AppBadge } from './AppBadge';
import { statusBadgeMeta } from './appBadgeConstants';
import type { StatusBadgeType, StatusPresetGroup } from './appBadgeTypes';

interface StatusBadgeProps {
  /** Extra classes for the pill. */
  className?: string;
  /**
   * Preset group to resolve `type` in. Needed when a value means different
   * things per group — `pending` is "Pending" for a sale but "Invited" for an
   * invitation — and harmless elsewhere.
   */
  group?: StatusPresetGroup;
  type: StatusBadgeType;
}

/**
 * Status pill for any status enum group (Payout, Workspace, Membership, etc.).
 * Renders the matching preset, or a neutral title-cased pill when the value has
 * no preset — records can carry statuses outside the SDK enum (legacy data,
 * SDK drift), and those must still show a badge rather than an empty cell.
 */
export const StatusBadge = ({ className, group, type }: StatusBadgeProps) => {
  if (!type) return null;

  const badgeType = statusBadgeMeta(type, group);

  // Records can carry statuses outside the SDK enum (legacy data, SDK drift);
  // that title-cased label is computed at runtime, so it can never be one of
  // `AppBadgePreset`'s literal preset values. Render it via `Badge` directly,
  // matching `AppBadge`'s own foundation styling, rather than widen the guard
  // that keeps `AppBadge` locked to real presets.
  if (!badgeType) {
    return (
      <Badge
        className={className}
        hasIndicator
        style="foundation"
        variant="default"
      >
        {toTitleCase(type)}
      </Badge>
    );
  }

  return <AppBadge className={className} type={badgeType} />;
};
